/**
 * /feeds/$slug — full feed source detail page.
 *
 * Sections:
 *   - Onboarding banner (4-step wizard) — dismissable, stored in localStorage.
 *   - Overview section-card (connector/format/schedule/credential/config).
 *   - Mapping section-card (Monaco YAML editor + fields panel + preview tabs).
 *   - Samples section-card (table + view body + force-delete).
 *   - Runs section-card (last N runs + click-through to RunDetailDialog).
 *   - Errors section-card (top 50 latest errors across recent runs).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { parse as parseYAML, stringify as stringifyYAML } from "yaml";
import {
    ArrowRight,
    CheckCircle2,
    Circle,
    Eye,
    FileText,
    History,
    Loader2,
    Pause,
    Play,
    Rss,
    Save,
    ShieldX,
    Sparkles,
    Trash2,
    X,
    Zap,
} from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import {
    type FeedMapping,
    type FeedSourceDetail,
    type PreviewResponse,
    type RunSummary,
    type SampleMeta,
    defaultMappingFor,
    useDeleteSample,
    useFeedRuns,
    useFeedSamples,
    useFeedSource,
    useFetchSample,
    usePauseFeed,
    usePreviewMapping,
    useReplayFeed,
    useResumeFeed,
    useUpdateFeedDetail,
} from "~/lib/feeds";
import { MappingEditor } from "~/components/MappingEditor";
import { PromoteMappingDialog } from "~/components/PromoteMappingDialog";
import { RunDetailDialog } from "~/components/RunDetailDialog";
import { SampleViewerDialog } from "~/components/SampleViewerDialog";
import { TableSkeleton } from "~/components/TableSkeleton";

// ---------- helpers ----------

function formatRelative(iso?: string): string {
    if (!iso) return "never";
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return iso;
    const diff = Date.now() - ts;
    if (diff < 0) return "scheduled";
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
}

function humanBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function humanInterval(secs: number): string {
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.round(secs / 60)}m`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h`;
    return `${Math.round(secs / 86400)}d`;
}

function statusPill(s: string): string {
    if (s === "ok" || s === "active") return "status-pill--live";
    if (s === "partial") return "status-pill--paused";
    if (s === "paused") return "status-pill--paused";
    if (s === "error") return "status-pill--failed";
    if (s === "running" || s === "queued") return "status-pill--queued";
    return "status-pill--draft";
}

function mappingToYAML(m?: FeedMapping): string {
    if (!m) return "";
    try {
        return stringifyYAML(m, { indent: 2 });
    } catch {
        return "";
    }
}

function yamlToMapping(y: string): FeedMapping | Error {
    if (!y.trim()) return { target: "ioc", fields: {} };
    try {
        const parsed = parseYAML(y);
        if (typeof parsed !== "object" || parsed === null) {
            return new Error("mapping must be a YAML object");
        }
        return parsed as FeedMapping;
    } catch (e) {
        return e instanceof Error ? e : new Error("YAML parse failed");
    }
}

// ---------- page ----------

export function FeedDetailPage(): React.ReactElement {
    const { slug } = useParams({ from: "/app/feeds/$slug" });
    const me = useCurrentUser();
    const detail = useFeedSource(slug);
    const samples = useFeedSamples(slug);
    const runs = useFeedRuns(slug);

    // Monaco editor state — YAML string the operator edits.
    const [yaml, setYaml] = useState<string>("");
    const [yamlError, setYamlError] = useState<string | null>(null);
    const yamlSeededRef = useRef(false);

    // Preview state.
    const [preview, setPreview] = useState<PreviewResponse | null>(null);
    const [previewTab, setPreviewTab] = useState<"rows" | "targets" | "errors">("rows");
    const [selectedSampleId, setSelectedSampleId] = useState<string>("");

    // Dialog state.
    const [sampleViewer, setSampleViewer] = useState<SampleMeta | null>(null);
    const [runDialog, setRunDialog] = useState<RunSummary | null>(null);

    // Mutations.
    const fetchSample = useFetchSample(slug);
    const previewMapping = usePreviewMapping(slug);
    const deleteSample = useDeleteSample(slug);
    const pause = usePauseFeed(slug);
    const resume = useResumeFeed(slug);
    const replay = useReplayFeed(slug);
    const update = useUpdateFeedDetail(slug);

    // Seed editor with draft (or active, fallback empty) on first load.
    useEffect(() => {
        if (yamlSeededRef.current || !detail.data) return;
        const source = detail.data.mappingDraft ?? detail.data.mappingActive;
        if (source) {
            setYaml(mappingToYAML(source));
        } else {
            // Brand-new feed — seed from defaultMappingFor and save as draft.
            const seed = defaultMappingFor(detail.data.connectorKind, detail.data.formatKind);
            setYaml(mappingToYAML(seed));
        }
        yamlSeededRef.current = true;
    }, [detail.data]);

    // Auto-select latest live sample when samples arrive.
    useEffect(() => {
        if (selectedSampleId || !samples.data || samples.data.length === 0) return;
        const live = samples.data.find((s) => s.status === "live") ?? samples.data[0];
        setSelectedSampleId(live.id);
    }, [samples.data, selectedSampleId]);

    const parsedMapping = useMemo(() => yamlToMapping(yaml), [yaml]);

    useEffect(() => {
        if (parsedMapping instanceof Error) {
            setYamlError(parsedMapping.message);
        } else {
            setYamlError(null);
        }
    }, [parsedMapping]);

    const mapping = parsedMapping instanceof Error ? null : parsedMapping;

    // ---- actions ----
    const runPreview = useCallback(
        async (sampleId?: string, overrideMapping?: FeedMapping): Promise<void> => {
            const sid = sampleId ?? selectedSampleId;
            if (!sid) {
                toast.error("Fetch a sample first");
                return;
            }
            const m = overrideMapping ?? mapping ?? undefined;
            if (!m) {
                toast.error(yamlError ?? "Fix YAML errors before previewing");
                return;
            }
            try {
                const res = await previewMapping.mutateAsync({ sampleId: sid, mapping: m });
                setPreview(res);
            } catch (err) {
                toast.error(err instanceof ApiError ? err.message : "Preview failed");
            }
        },
        [selectedSampleId, mapping, yamlError, previewMapping],
    );

    const runFetchAndPreview = useCallback(async (): Promise<void> => {
        try {
            const res = await fetchSample.mutateAsync();
            toast.success(`Sample captured (${humanBytes(res.byteCount)})`);
            setSelectedSampleId(res.id);
            await runPreview(res.id);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Fetch failed");
        }
    }, [fetchSample, runPreview]);

    const saveDraft = useCallback(async (): Promise<void> => {
        if (!mapping) {
            toast.error(yamlError ?? "Fix YAML errors first");
            return;
        }
        try {
            await update.mutateAsync({ mappingDraft: mapping });
            toast.success("Draft saved");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Save failed");
        }
    }, [mapping, update, yamlError]);

    const revertToActive = useCallback(() => {
        if (!detail.data?.mappingActive) {
            toast.error("No active mapping to revert to");
            return;
        }
        setYaml(mappingToYAML(detail.data.mappingActive));
        toast.success("Reverted to active mapping");
    }, [detail.data?.mappingActive]);

    // ---- render ----
    if (me.data && !me.data.roles.some((r) => r === "admin" || r === "operator")) {
        return (
            <div className="empty-state">
                <ShieldX size={20} className="empty-state__icon" aria-hidden />
                <div className="empty-state__title">Forbidden</div>
            </div>
        );
    }

    if (detail.isLoading) {
        return <TableSkeleton rows={8} columns={4} />;
    }
    if (detail.isError || !detail.data) {
        return (
            <div className="empty-state">
                <ShieldX size={20} className="empty-state__icon" aria-hidden />
                <div className="empty-state__title">Could not load feed</div>
            </div>
        );
    }

    const d = detail.data;
    const hasDraft = !!d.mappingDraft;
    const promoteReady =
        hasDraft && !yamlError && preview != null && preview.targets.length > 0;

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon"><Rss size={14} aria-hidden /></span>
                            Data intake / {d.slug}
                        </span>
                        <h1 className="page-header__title">{d.name}</h1>
                        <div className="cluster-xs">
                            <span className={`status-pill ${statusPill(d.status)}`}>{d.status}</span>
                            {d.autoPaused && <span className="chip chip--danger">auto-paused</span>}
                            <span className="chip">{d.connectorKind}</span>
                            <span className="chip">{d.formatKind}</span>
                            <span className="chip">mapping v{d.mappingVersion}</span>
                        </div>
                    </div>
                    <div className="page-header__actions cluster-xs">
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => void (d.enabled ? pause.mutateAsync() : resume.mutateAsync())}
                            disabled={pause.isPending || resume.isPending}
                            aria-pressed={!d.enabled}
                        >
                            {d.enabled ? <Pause size={14} /> : <Play size={14} />}
                            <span>{d.enabled ? "Pause" : "Resume"}</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() =>
                                void replay.mutateAsync().then(() => toast.success("Replay queued"))
                                    .catch((e: unknown) => toast.error(e instanceof ApiError ? e.message : "Replay failed"))
                            }
                            disabled={replay.isPending}
                        >
                            <Zap size={14} />
                            <span>Replay</span>
                        </button>
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {d.ingestedCount.toLocaleString()} ingested lifetime
                    </span>
                    <span className="page-header__meta__item tabular-nums">
                        last run {formatRelative(d.lastRunAt)}
                    </span>
                    <span className="page-header__meta__item tabular-nums">
                        {d.consecutiveFailures}/{d.circuitBreakerThreshold} consecutive failures
                    </span>
                </div>
            </header>

            <OnboardingBanner
                slug={slug}
                hasSample={!!samples.data && samples.data.length > 0}
                hasPreview={!!preview}
                hasMappedTargets={!!preview && preview.targets.length > 0}
                hasActiveMapping={!!d.mappingActive}
                onFetchSample={runFetchAndPreview}
                fetching={fetchSample.isPending || previewMapping.isPending}
            />

            <Overview detail={d} onUpdate={(p) => void update.mutateAsync(p)} />

            <section className="section-card" aria-label="Mapping">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Mapping</h2>
                        <p className="section-card__subtitle">
                            YAML on the left, quick-fields on the right. ⌘S saves the draft.
                        </p>
                    </div>
                    <div className="section-card__actions cluster-xs">
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={revertToActive}
                            disabled={!d.mappingActive}
                        >
                            <History size={14} />
                            <span>Revert to active</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => void saveDraft()}
                            disabled={update.isPending || !!yamlError}
                        >
                            {update.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            <span>Save draft</span>
                        </button>
                        <PromoteMappingDialog
                            slug={slug}
                            disabled={!promoteReady}
                        />
                    </div>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 2fr) minmax(240px, 1fr)",
                        gap: "var(--space-4)",
                    }}
                >
                    <div className="stack-sm">
                        <MappingEditor value={yaml} onChange={setYaml} onSave={() => void saveDraft()} />
                        {yamlError && (
                            <div
                                className="chip chip--danger"
                                role="alert"
                                style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                            >
                                {yamlError}
                            </div>
                        )}
                    </div>
                    <FieldsPanel yaml={yaml} onYamlChange={setYaml} />
                </div>

                <div className="cluster-xs" style={{ marginTop: "var(--space-4)", justifyContent: "space-between" }}>
                    <div className="cluster-xs">
                        <label className="toolbar__filter">
                            <span className="toolbar__filter__label">Sample</span>
                            <select
                                className="input"
                                value={selectedSampleId}
                                onChange={(e) => setSelectedSampleId(e.target.value)}
                            >
                                <option value="">— none —</option>
                                {(samples.data ?? []).map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {formatRelative(s.capturedAt)} — {humanBytes(s.byteCount)} ({s.status})
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            className="btn btn--primary press-crush"
                            onClick={() => void runFetchAndPreview()}
                            disabled={fetchSample.isPending}
                        >
                            {fetchSample.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                            <span>Fetch sample → Preview</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => void runPreview()}
                            disabled={previewMapping.isPending || !selectedSampleId || !!yamlError}
                        >
                            {previewMapping.isPending ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                            <span>Preview</span>
                        </button>
                    </div>
                </div>

                <PreviewPanel
                    preview={preview}
                    loading={previewMapping.isPending}
                    tab={previewTab}
                    setTab={setPreviewTab}
                />
            </section>

            <section className="section-card" aria-label="Samples">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Samples</h2>
                        <p className="section-card__subtitle">
                            Retained {d.sampleRetentionDays} days. Live samples power the preview
                            dry-run — expired are kept for audit.
                        </p>
                    </div>
                </div>
                {samples.isLoading && <TableSkeleton rows={3} columns={5} />}
                {samples.data && samples.data.length === 0 && (
                    <div className="empty-state">
                        <FileText size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No samples yet</div>
                        <div className="empty-state__body">Click “Fetch sample” to capture one.</div>
                    </div>
                )}
                {samples.data && samples.data.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Captured</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap", textAlign: "right" }}>Size</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Content type</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Status</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap", textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {samples.data.map((s) => (
                                    <tr key={s.id}>
                                        <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {formatRelative(s.capturedAt)}
                                        </td>
                                        <td className="text-12 tabular-nums" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                                            {humanBytes(s.byteCount)}
                                        </td>
                                        <td className="type-mono-sm text-12" style={{ whiteSpace: "nowrap" }}>
                                            {s.contentType || "—"}
                                        </td>
                                        <td>
                                            <span className={`status-pill ${s.status === "live" ? "status-pill--live" : "status-pill--queued"}`}>
                                                {s.status}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                            <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                                                <button
                                                    type="button"
                                                    className="btn btn--ghost btn--sm"
                                                    onClick={() => setSampleViewer(s)}
                                                    aria-label="View body"
                                                >
                                                    <Eye size={12} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn--ghost btn--sm"
                                                    onClick={() =>
                                                        void deleteSample.mutateAsync(s.id)
                                                            .then(() => toast.success("Sample deleted"))
                                                            .catch((e: unknown) => toast.error(e instanceof ApiError ? e.message : "Delete failed"))
                                                    }
                                                    disabled={deleteSample.isPending}
                                                    aria-label="Delete sample"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="section-card" aria-label="Runs">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Recent runs</h2>
                        <p className="section-card__subtitle">
                            Last {runs.data?.length ?? 0} ingest runs. Click a row for errors.
                        </p>
                    </div>
                </div>
                {runs.isLoading && <TableSkeleton rows={5} columns={6} />}
                {runs.data && runs.data.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-state__title">No runs yet</div>
                        <div className="empty-state__body">
                            Promote a mapping and click Replay to trigger the first run.
                        </div>
                    </div>
                )}
                {runs.data && runs.data.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Started</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Duration</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Status</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Trigger</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap", textAlign: "right" }}>Rows</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap", textAlign: "right" }}>Dropped</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap", textAlign: "right" }}>Errors</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.data.map((r) => (
                                    <tr
                                        key={r.id}
                                        onClick={() => setRunDialog(r)}
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === "Enter") setRunDialog(r); }}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {formatRelative(r.startedAt)}
                                        </td>
                                        <td className="text-12 tabular-nums text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {runDuration(r)}
                                        </td>
                                        <td>
                                            <span className={`status-pill ${statusPill(r.status)}`}>{r.status}</span>
                                        </td>
                                        <td><span className="chip">{r.trigger}</span></td>
                                        <td className="text-12 tabular-nums" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                                            {r.rowsIn.toLocaleString()} <ArrowRight size={10} style={{ verticalAlign: "middle" }} /> {r.rowsOut.toLocaleString()}
                                        </td>
                                        <td className="text-12 tabular-nums text-text-muted" style={{ textAlign: "right" }}>
                                            {r.dropped.toLocaleString()}
                                        </td>
                                        <td className="text-12 tabular-nums text-text-muted" style={{ textAlign: "right" }}>
                                            {r.errors.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="section-card" aria-label="Errors">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Recent errors</h2>
                        <p className="section-card__subtitle">
                            Top {d.recentErrors.length} across recent runs — newest first.
                        </p>
                    </div>
                </div>
                {d.recentErrors.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state__title">Clean slate</div>
                        <div className="empty-state__body">No errors recorded.</div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Row</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Field</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Stage</th>
                                    <th>Reason</th>
                                    <th>Excerpt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.recentErrors.map((e, i) => (
                                    <tr key={i}>
                                        <td className="text-12 tabular-nums">{e.rowIndex}</td>
                                        <td className="type-mono-sm text-12">{e.field ?? "—"}</td>
                                        <td><span className="chip">{e.stage}</span></td>
                                        <td style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {e.reason}
                                        </td>
                                        <td
                                            className="type-mono-sm text-12 text-text-muted"
                                            style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        >
                                            {e.excerpt}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <SampleViewerDialog
                slug={slug}
                sample={sampleViewer}
                open={!!sampleViewer}
                onOpenChange={(o) => {
                    if (!o) setSampleViewer(null);
                }}
            />
            <RunDetailDialog
                slug={slug}
                run={runDialog}
                open={!!runDialog}
                onOpenChange={(o) => {
                    if (!o) setRunDialog(null);
                }}
            />
        </div>
    );
}

function runDuration(r: RunSummary): string {
    if (!r.finishedAt) return "running…";
    const d = new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime();
    if (d < 1000) return `${d}ms`;
    if (d < 60_000) return `${(d / 1000).toFixed(1)}s`;
    return `${Math.floor(d / 60_000)}m ${Math.floor((d % 60_000) / 1000)}s`;
}

// ---------- Overview panel ----------

function Overview({
    detail,
    onUpdate,
}: {
    detail: FeedSourceDetail;
    onUpdate: (p: { ingestWorkerConcurrency?: number; circuitBreakerThreshold?: number; sampleRetentionDays?: number; scheduleSeconds?: number; weight?: number }) => void;
}): React.ReactElement {
    return (
        <section className="section-card" aria-label="Overview">
            <div className="section-card__header">
                <div className="section-card__title-group">
                    <h2 className="section-card__title">Overview</h2>
                    <p className="section-card__subtitle">
                        Schedule, thresholds, and worker tuning.
                    </p>
                </div>
            </div>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "var(--space-4)",
                }}
            >
                <Field label="Connector" value={<span className="type-mono-sm">{detail.connectorKind}</span>} />
                <Field label="Format" value={<span className="type-mono-sm">{detail.formatKind}</span>} />
                <Field label="Schedule" value={`every ${humanInterval(detail.scheduleSeconds)}`} />
                <Field
                    label="Credential"
                    value={
                        detail.credentialRef ? (
                            <span className="type-mono-sm text-12">{detail.credentialRef}</span>
                        ) : (
                            <span className="text-text-subtle text-12">none</span>
                        )
                    }
                    sub="Edit in Vault / env config; not exposed via UI."
                />
                <Field
                    label="Tenant"
                    value={detail.tenantId ? <code className="type-mono-sm">{detail.tenantId}</code> : <span className="text-text-subtle text-12">platform-wide</span>}
                />
                <Field label="Trust weight" value={<span className="tabular-nums">{detail.weight}</span>} />
                <EditableNumber
                    label="Worker concurrency"
                    value={detail.ingestWorkerConcurrency}
                    min={1}
                    max={16}
                    slider
                    onCommit={(n) => onUpdate({ ingestWorkerConcurrency: n })}
                />
                <EditableNumber
                    label="Circuit breaker threshold"
                    value={detail.circuitBreakerThreshold}
                    min={1}
                    max={500}
                    onCommit={(n) => onUpdate({ circuitBreakerThreshold: n })}
                />
                <EditableNumber
                    label="Sample retention (days)"
                    value={detail.sampleRetentionDays}
                    min={1}
                    max={365}
                    onCommit={(n) => onUpdate({ sampleRetentionDays: n })}
                />
            </div>
        </section>
    );
}

function Field({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }): React.ReactElement {
    return (
        <div className="stack-xs">
            <span className="metric-card__label">{label}</span>
            <span className="text-13">{value}</span>
            {sub && <span className="text-11 text-text-subtle">{sub}</span>}
        </div>
    );
}

function EditableNumber({
    label,
    value,
    min,
    max,
    slider,
    onCommit,
}: {
    label: string;
    value: number;
    min: number;
    max: number;
    slider?: boolean;
    onCommit: (n: number) => void;
}): React.ReactElement {
    const [v, setV] = useState(value);
    useEffect(() => setV(value), [value]);
    return (
        <div className="stack-xs">
            <span className="metric-card__label tabular-nums">{label}: {v}</span>
            {slider ? (
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={v}
                    onChange={(e) => setV(Number(e.target.value))}
                    onMouseUp={() => v !== value && onCommit(v)}
                    onTouchEnd={() => v !== value && onCommit(v)}
                    aria-valuenow={v}
                    aria-valuemin={min}
                    aria-valuemax={max}
                />
            ) : (
                <input
                    type="number"
                    className="input tabular-nums"
                    min={min}
                    max={max}
                    value={v}
                    onChange={(e) => setV(Number(e.target.value) || min)}
                    onBlur={() => v !== value && onCommit(v)}
                />
            )}
        </div>
    );
}

// ---------- Onboarding banner ----------

function OnboardingBanner(props: {
    slug: string;
    hasSample: boolean;
    hasPreview: boolean;
    hasMappedTargets: boolean;
    hasActiveMapping: boolean;
    fetching: boolean;
    onFetchSample: () => Promise<void>;
}): React.ReactElement | null {
    const key = `feed-wizard-dismissed:${props.slug}`;
    const [dismissed, setDismissed] = useState<boolean>(() => {
        if (typeof window === "undefined") return false;
        return window.localStorage.getItem(key) === "1";
    });
    if (dismissed) return null;

    // Auto-hide once everything is already live.
    if (props.hasActiveMapping && props.hasMappedTargets) return null;

    const steps = [
        { label: "Fetch sample",   done: props.hasSample },
        { label: "Parse preview",  done: props.hasPreview },
        { label: "Map fields",     done: props.hasMappedTargets },
        { label: "Promote",        done: props.hasActiveMapping },
    ];
    const currentIdx = steps.findIndex((s) => !s.done);

    return (
        <section
            className="section-card section-card--highlight"
            aria-label="Onboarding steps"
            style={{ position: "relative" }}
        >
            <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                    window.localStorage.setItem(key, "1");
                    setDismissed(true);
                }}
                style={{ position: "absolute", top: "var(--space-3)", right: "var(--space-3)" }}
                aria-label="Dismiss onboarding"
            >
                <X size={12} />
            </button>
            <div className="section-card__header">
                <div className="section-card__title-group">
                    <h2 className="section-card__title">
                        <Sparkles size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                        Get ingesting in under 5 minutes
                    </h2>
                    <p className="section-card__subtitle">
                        Four steps. The wizard auto-fills a starter mapping based on the
                        connector + format you picked.
                    </p>
                </div>
            </div>
            <ol
                className="cluster-sm"
                style={{ listStyle: "none", padding: 0, margin: 0, gap: "var(--space-4)" }}
            >
                {steps.map((s, i) => {
                    const done = s.done;
                    const current = i === currentIdx;
                    return (
                        <li
                            key={s.label}
                            className="cluster-xs text-13"
                            style={{
                                gap: "var(--space-2)",
                                color: done ? "var(--color-success)" : current ? "var(--color-text)" : "var(--color-text-subtle)",
                                fontWeight: current ? "var(--font-weight-semibold)" : "var(--font-weight-regular)",
                            }}
                            aria-current={current ? "step" : undefined}
                        >
                            {done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                            <span className="tabular-nums">{i + 1}</span>
                            <span>{s.label}</span>
                        </li>
                    );
                })}
            </ol>
            {currentIdx === 0 && (
                <div className="cluster-xs" style={{ marginTop: "var(--space-4)" }}>
                    <button
                        type="button"
                        className="btn btn--primary press-crush"
                        onClick={() => void props.onFetchSample()}
                        disabled={props.fetching}
                    >
                        {props.fetching ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        <span>Fetch sample → Preview</span>
                    </button>
                </div>
            )}
        </section>
    );
}

// ---------- Preview panel ----------

function PreviewPanel({
    preview,
    loading,
    tab,
    setTab,
}: {
    preview: PreviewResponse | null;
    loading: boolean;
    tab: "rows" | "targets" | "errors";
    setTab: (t: "rows" | "targets" | "errors") => void;
}): React.ReactElement {
    if (loading) {
        return <div style={{ marginTop: "var(--space-4)" }}><TableSkeleton rows={4} columns={3} /></div>;
    }
    if (!preview) {
        return (
            <div className="empty-state" style={{ marginTop: "var(--space-4)" }}>
                <div className="empty-state__title">No preview yet</div>
                <div className="empty-state__body">
                    Fetch a sample and click Preview to dry-run your mapping.
                </div>
            </div>
        );
    }

    return (
        <div aria-live="polite" style={{ marginTop: "var(--space-4)" }}>
            <div className="cluster-xs" role="tablist" aria-label="Preview tabs">
                <TabButton active={tab === "rows"} onClick={() => setTab("rows")}>
                    Parsed rows ({preview.rows.length})
                </TabButton>
                <TabButton active={tab === "targets"} onClick={() => setTab("targets")}>
                    Mapped targets ({preview.targets.length})
                </TabButton>
                <TabButton active={tab === "errors"} onClick={() => setTab("errors")}>
                    Errors ({preview.errors.length})
                </TabButton>
                <span className="text-12 text-text-subtle tabular-nums" style={{ marginLeft: "auto" }}>
                    {preview.dropped.toLocaleString()} dropped
                </span>
            </div>

            <div style={{ marginTop: "var(--space-3)", overflowX: "auto" }}>
                {tab === "rows" && <RowsTable rows={preview.rows} />}
                {tab === "targets" && <TargetsTable targets={preview.targets} />}
                {tab === "errors" && <ErrorsTable errors={preview.errors} />}
            </div>
        </div>
    );
}

function TabButton({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            className={active ? "btn btn--primary btn--sm" : "btn btn--ghost btn--sm"}
        >
            {children}
        </button>
    );
}

function RowsTable({ rows }: { rows: PreviewResponse["rows"] }): React.ReactElement {
    if (rows.length === 0) return <div className="text-12 text-text-muted">No rows parsed.</div>;
    // Infer column keys from the union of first 10 rows.
    const keySet = new Set<string>();
    rows.slice(0, 10).forEach((r) => {
        if (r.data && typeof r.data === "object") {
            Object.keys(r.data).forEach((k) => keySet.add(k));
        }
    });
    const keys = Array.from(keySet).slice(0, 8);
    return (
        <table className="data-table">
            <thead>
                <tr>
                    <th style={{ width: 1 }}>#</th>
                    {keys.map((k) => (<th key={k} className="type-mono-sm">{k}</th>))}
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>
                        <td className="tabular-nums text-12">{r.number}</td>
                        {keys.map((k) => (
                            <td
                                key={k}
                                className="type-mono-sm text-12"
                                style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={String((r.data as Record<string, unknown>)?.[k] ?? "")}
                            >
                                {String((r.data as Record<string, unknown>)?.[k] ?? "")}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function TargetsTable({ targets }: { targets: PreviewResponse["targets"] }): React.ReactElement {
    if (targets.length === 0) return <div className="text-12 text-text-muted">No targets emitted.</div>;
    return (
        <table className="data-table">
            <thead>
                <tr>
                    <th style={{ width: 1 }}>Target</th>
                    <th style={{ width: 1 }}>Type</th>
                    <th>Value</th>
                    <th>Tags</th>
                </tr>
            </thead>
            <tbody>
                {targets.map((t, i) => (
                    <tr key={i}>
                        <td><span className="chip">{String(t.target ?? "")}</span></td>
                        <td className="type-mono-sm text-12">{String(t.type ?? t.cveId ?? "")}</td>
                        <td
                            className="type-mono-sm text-12"
                            style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={String(t.value ?? "")}
                        >
                            {String(t.value ?? "")}
                        </td>
                        <td className="text-12 text-text-muted">
                            {Array.isArray(t.tags) ? (t.tags as string[]).join(", ") : ""}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function ErrorsTable({ errors }: { errors: PreviewResponse["errors"] }): React.ReactElement {
    if (errors.length === 0) return <div className="text-12 text-text-muted">No errors.</div>;
    return (
        <table className="data-table">
            <thead>
                <tr>
                    <th style={{ width: 1 }}>Row</th>
                    <th style={{ width: 1 }}>Field</th>
                    <th>Reason</th>
                </tr>
            </thead>
            <tbody>
                {errors.map((e, i) => (
                    <tr key={i}>
                        <td className="tabular-nums text-12">{e.rowIndex}</td>
                        <td className="type-mono-sm text-12">{e.field ?? "—"}</td>
                        <td style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {e.reason}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// ---------- Fields side-panel ----------

const FIELD_ORDER: { key: string; label: string; hint?: string; slot: "top" | "fields" | "source_values" }[] = [
    { key: "target",        label: "target",       hint: "ioc | cve | report | submission", slot: "top" },
    { key: "when",          label: "when",         hint: "emit only if expression is truthy", slot: "top" },
    { key: "drop_when",     label: "drop_when",    hint: "skip rows where expression is truthy", slot: "top" },
    { key: "type",          label: "fields.type",  slot: "fields" },
    { key: "value",         label: "fields.value", slot: "fields" },
    { key: "tags",          label: "fields.tags",  slot: "fields" },
    { key: "severity",      label: "fields.severity", slot: "fields" },
    { key: "killchain_phase", label: "fields.killchain_phase", slot: "fields" },
    { key: "first_seen",    label: "fields.first_seen",  slot: "fields" },
    { key: "last_seen",     label: "fields.last_seen",   slot: "fields" },
];

/**
 * The fields panel edits named keys in the mapping doc. We parse the current
 * YAML into a mapping object, mutate the specific slot, then re-stringify —
 * this loses comments (YAML Document API would preserve them, but for our
 * wizard a clean re-emit is acceptable and keeps the UX simple).
 */
function FieldsPanel({
    yaml,
    onYamlChange,
}: {
    yaml: string;
    onYamlChange: (y: string) => void;
}): React.ReactElement {
    const parsed = useMemo(() => {
        const m = yamlToMapping(yaml);
        return m instanceof Error ? null : m;
    }, [yaml]);

    function set(key: string, slot: "top" | "fields" | "source_values", v: string): void {
        const current = parsed ? { ...parsed } : { target: "ioc", fields: {} as Record<string, string> };
        if (slot === "top") {
            (current as Record<string, string>)[key] = v;
        } else {
            const sub: Record<string, string> = { ...(current[slot] ?? {}) };
            if (v === "") {
                delete sub[key];
            } else {
                sub[key] = v;
            }
            (current as Record<string, unknown>)[slot] = sub;
        }
        onYamlChange(stringifyYAML(current, { indent: 2 }));
    }

    function get(key: string, slot: "top" | "fields" | "source_values"): string {
        if (!parsed) return "";
        if (slot === "top") return String((parsed as Record<string, unknown>)[key] ?? "");
        const sub = (parsed[slot] ?? {}) as Record<string, string>;
        return sub[key] ?? "";
    }

    return (
        <aside
            className="stack-sm"
            style={{
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-3)",
                background: "var(--color-surface-sunken)",
                maxHeight: 400,
                overflowY: "auto",
            }}
            aria-label="Quick fields"
        >
            <div className="text-12 text-text-muted" style={{ textTransform: "uppercase", letterSpacing: "var(--letter-spacing-wide)" }}>
                Quick fields
            </div>
            {FIELD_ORDER.map(({ key, label, hint, slot }) => (
                <label key={label} className="stack-xs">
                    <span className="text-11 text-text-muted type-mono-sm">{label}</span>
                    <input
                        className="input"
                        value={get(key, slot)}
                        onChange={(e) => set(key, slot, e.target.value)}
                        placeholder={hint ?? "expression"}
                        style={{ fontFamily: "var(--font-family-mono)", fontSize: "var(--font-size-12)" }}
                    />
                </label>
            ))}
            <div className="text-11 text-text-subtle">
                source_values.* is edited in the YAML directly.
            </div>
        </aside>
    );
}
